/**
 * 온보딩 체크리스트 — 완료 상태는 브라우저 localStorage에만 둔다(서버 저장 없음).
 * 항목은 "누가 해줘야 하는 것"과 "내가 하면 되는 것"을 섞지 않고 `ask`로 구분한다.
 */
export interface ChecklistItem {
  id: string;
  label: string;
  detail: string;
  /** 남에게 요청해야 풀리는 항목이면 요청처. 내가 직접 할 수 있으면 비운다. */
  ask?: string;
}

export interface ChecklistGroup {
  key: string;
  title: string;
  when: string;
  items: ChecklistItem[];
}

export const CHECKLIST: ChecklistGroup[] = [
  {
    key: 'access',
    title: '🔑 계정 · 접근 권한',
    when: '첫날 — 여기서 막히면 이후 전부가 막히니 제일 먼저 요청해 주세요',
    items: [
      { id: 'access-google', label: '회사 구글 계정', detail: '사내 도구 대부분이 이 계정으로 로그인합니다', ask: '인사·경영지원' },
      { id: 'access-slack', label: '슬랙 워크스페이스 + 알림 채널', detail: '장애 1차 통보가 슬랙으로 옵니다. 개발·알림 채널에 꼭 들어가 주세요', ask: '팀 리드' },
      { id: 'access-github', label: 'GitHub nd-market org 멤버', detail: '저장소가 전부 private이라 초대 전에는 클론이 안 됩니다. 이사가 끝나기 전까지 NDMARKET 계정에 남은 저장소는 저장소별 협업자 초대가 따로 필요합니다', ask: '팀 리드' },
      { id: 'access-notion', label: '노션 워크스페이스', detail: '기획·정책·회의록이 모여 있습니다', ask: '팀 리드' },
      { id: 'access-gcp', label: 'GCP IAM (ndmarket-dev / ndmarket-prod)', detail: 'dev는 넓게, prod는 조회 위주로 받습니다', ask: '인프라 담당' },
      { id: 'access-grafana', label: 'Grafana · Kibana', detail: '지표와 로그 — 장애 대응의 출발점입니다', ask: '인프라 담당' },
      { id: 'access-sentry', label: 'Sentry 조직 초대', detail: '초대 전에는 프로젝트 목록이 비어 보입니다', ask: '인프라 담당' },
      { id: 'access-argocd', label: 'ArgoCD', detail: '배포 상태를 확인합니다. 배포 조작 권한은 나중에 따로 받습니다', ask: '인프라 담당' },
      { id: 'access-admin', label: '어드민 계정 (dev)', detail: '운영 화면에서 데이터를 직접 만들어 봐야 도메인이 잡힙니다', ask: '운영팀' },
    ],
  },
  {
    key: 'machine',
    title: '💻 개발 머신',
    when: '첫날~이틀차 — 콘솔이 뜨면 환경은 대체로 정상입니다',
    items: [
      { id: 'machine-tools', label: 'nvm · colima · gcloud · gh 설치', detail: '개발 환경 화면의 설치 목록을 그대로 따라 해 주세요' },
      { id: 'machine-clone', label: '워크스페이스 저장소 클론', detail: '`clone-missing-repos.sh`로 한 번에 받을 수 있습니다' },
      { id: 'machine-console', label: '개발자 콘솔 로컬 기동', detail: '`npm run dev:web` → localhost:4700에서 열립니다' },
      { id: 'machine-api', label: 'nd-market-api 로컬 기동', detail: '`.env.local`을 받아 넣고 `npm run start:local`을 실행합니다' },
      { id: 'machine-front', label: '프런트를 로컬 API에 연결', detail: 'vue_retail에서 로그인까지 되면 통과입니다 🎉' },
      { id: 'machine-db', label: 'cloud-sql-proxy로 dev DB 조회', detail: '3306/3307은 이 용도로만 써 주세요' },
    ],
  },
  {
    key: 'context',
    title: '🧭 맥락 잡기',
    when: '첫 주 — 코드를 고치기 전에 지형부터 살펴봅니다',
    items: [
      { id: 'ctx-map', label: '서비스맵 훑기', detail: '개발자 콘솔 PoC → 서비스맵에서 어떤 서비스가 무엇을 부르는지 볼 수 있습니다' },
      { id: 'ctx-workspace', label: '워크스페이스 루트 CLAUDE.md 읽기', detail: '저장소 목록과 역할이 여기 정리돼 있습니다' },
      { id: 'ctx-repo-docs', label: '담당 저장소의 CONTEXT.md · docs 읽기', detail: '탐색보다 문서가 빠릅니다. 특히 도메인 문서를 먼저 봐 주세요' },
      { id: 'ctx-conventions', label: '코딩 컨벤션 문서 읽기', detail: 'lint가 잡아주지 않는 규칙이 많습니다 — 리뷰에서 되돌아오는 지적 대부분이 여기 있습니다' },
      { id: 'ctx-board', label: '프로젝트 보드에서 진행 중 과제 보기', detail: '팀이 지금 무엇을 하고 있는지 한눈에 볼 수 있습니다' },
    ],
  },
  {
    key: 'firstpr',
    title: '🚀 첫 변경 흘려보내기',
    when: '첫 주 안에 — 작아도 좋으니 파이프라인 전체를 한 번 통과시켜 봅니다',
    items: [
      { id: 'pr-issue', label: '작은 이슈 하나 배정받기', detail: '문서 오타·로그 개선처럼 리스크가 낮은 것으로 시작합니다', ask: '팀 리드' },
      { id: 'pr-branch', label: '규약대로 브랜치·커밋 만들기', detail: '브랜치·커밋 메시지에 작업 식별자를 넣어 주세요' },
      { id: 'pr-review', label: 'PR 올리고 리뷰 받기', detail: '리뷰에서 나온 컨벤션 지적을 메모해 두면 다음 PR이 훨씬 수월합니다' },
      { id: 'pr-deploy', label: '머지 후 배포까지 눈으로 따라가기', detail: 'GitHub Actions → ArgoCD → Grafana에서 내 변경이 나가는 걸 확인합니다' },
    ],
  },
];
